/**
 * PR #41 final hardening — Phase 7 (AI Assistant context handoff
 * contract).
 *
 * NOT AI add-on selling — that stays out of scope for this pass. This
 * is only the stable shape a future AI Assistant integration would
 * consume, assembled here from state RetailCustomer.jsx already owns,
 * and persisted to a tenant-scoped sessionStorage slot so it's
 * available the moment a future consumer wants it, without inventing a
 * second identity/address/cart system.
 *
 * Privacy: the validated shipping address is never exposed in full —
 * only country/state/postal-prefix (matches the "never expose address
 * details in logs" rule this milestone's Phase 4 already established
 * for the address-validation flow; the same discipline applies here).
 */

const CONTEXT_KEY = "retail_ai_context_v1";

function contextKey(slug) {
  return `${CONTEXT_KEY}:${slug || "unknown"}`;
}

// Truncates a full ZIP to its first 3 digits — enough for a region-level
// AI recommendation (e.g. shipping-time estimates) without carrying a
// customer's exact postal code into assistant context.
function postalPrefix(postal) {
  const digits = String(postal || "").replace(/\D/g, "");
  return digits ? digits.slice(0, 3) : null;
}

/**
 * Builds the stable AI Assistant context object. Every field is
 * optional/nullable on purpose — this must be safe to call at ANY point
 * in the storefront lifecycle (before recognition, before an address
 * exists, before a cart has items), never throwing and never fabricating
 * a value that isn't actually known yet.
 */
export function buildAssistantContext({
  slug, catalogId, catalogName, linkSlug,
  customer, orderType, cart, shippingSelection,
} = {}) {
  const shippingAddr = customer?.shippingAddress || customer?.deliveryAddress || null;
  const region = shippingAddr
    ? {
        country: shippingAddr.country || shippingAddr.region_country || null,
        state: shippingAddr.state || shippingAddr.region || null,
        postal_prefix: postalPrefix(shippingAddr.postal_code || shippingAddr.postal),
      }
    : null;

  return {
    // The public storefront is never given the internal `company_id` —
    // `slug` is the client-safe tenant identifier; a backend consumer of
    // this context resolves slug -> company_id the same way
    // `retail.assistant.resolve_public_tenant` already does everywhere
    // else, rather than the frontend fabricating an id it was never given.
    slug: slug || null,
    catalog_id: catalogId || null,
    catalog_name: catalogName || null,
    link_slug: linkSlug || null,
    customer_id: customer?.id || null,
    customer_recognized: Boolean(customer?.id),
    fulfillment_mode: orderType || null,
    validated_address_region: region,
    cart: {
      items: Array.isArray(cart?.items)
        ? cart.items.map((it) => ({ product_id: it.product_id, qty: Number(it.quantity || 0) }))
        : [],
      subtotal: Number(cart?.subtotal || 0),
    },
    shipping: {
      quote_selected: Boolean(shippingSelection),
      service_code: shippingSelection?.service_code || null,
      customer_charge: Number(shippingSelection?.rate_amount || 0),
    },
  };
}

export function saveAssistantContext(slug, context) {
  try {
    sessionStorage.setItem(contextKey(slug), JSON.stringify(context));
  } catch (_e) { /* noop — best-effort session persistence */ }
}

export function loadAssistantContext(slug) {
  try {
    const raw = sessionStorage.getItem(contextKey(slug));
    return raw ? JSON.parse(raw) : null;
  } catch (_e) {
    return null;
  }
}
